import React, { useCallback, useEffect, useState } from "react";
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import ReportButton from "../Common/ReportButton";
import BarterExchangeSection from "../Common/BarterExchangeSection";
import { COMMUNITY_TYPES, LAND_USE_TYPES } from "./CreateCommunityPost";
import CommunityComments from "./CommunityComments";
import { requireVerifiedEmail } from "../../utils/emailVerification";
import { useNavigate } from "react-router-dom";
import { getLandAvailabilityLabel } from "../../constants/availability";
import {
  LOCATION_AREA_ERROR,
  buildBroadLocationFromGoogleResult,
  normalizeBroadLocation,
  validateBroadLocationInput,
} from "../../utils/locationArea";
import {
  LOCATION_SOURCES,
  buildCanonicalLocationPayload,
  getSafeDistanceMiles,
  getLocationDiagnostics,
  hasValidCoordinates,
  normalizeLocationRecord,
  normalizeProfileLocation,
  selectCommunityEditLocation,
} from "../../utils/locationUtils";
import {
  DEFAULT_BARTER_FIELDS,
  WANT_AD_URGENCY_OPTIONS,
  WANTED_CATEGORY_OPTIONS,
  buildBarterPayload,
  getBarterCategoryLabel,
  getExchangeBadges,
  getWantedCategoryLabel,
  normalizeBarterFields,
} from "../../utils/barter";
import FarmAvatar from "../Common/FarmAvatar";
import { getProfileRoleLabel } from "../../utils/profileRoles";
import { getProfilePath } from "../../utils/storefront";
import HatTipReaction from "./HatTipReaction";
import {
  getHatTipAvatarReactions,
  getHatTipDisplayName,
  getHatTipSummary,
} from "../../utils/hatTipSocialProof";
import { getCommentCountLabel } from "../../utils/communityComments";
import { getCommunityPostUrl } from "../../utils/communityLinks";
import {
  getCommunityAlertExpiryLabel,
  getCommunityAlertStatusLabel,
  isActiveCommunityAlert,
  isCommunityAlertPost,
  isResolvedCommunityAlert,
} from "../../utils/communityAlerts";

const LEGACY_LOCATION_PROFILE_DISTANCE_THRESHOLD_MILES = 100;

const getTypeLabel = (type) => {
  return COMMUNITY_TYPES.find((item) => item.value === type)?.label || "General";
};

const landUseTypeLabels = {
  horse_grazing: "Horse grazing pasture",
  livestock_grazing: "Livestock grazing",
  fenced_dog_run: "Fenced dog run",
  garden_plot: "Garden plot",
  hay_field_access: "Hay field access",
  equipment_parking: "Equipment parking",
  temporary_holding: "Temporary holding area",
  other: "Other land use",
};

const formatUnknownBoolean = (value) => {
  if (value === true || value === "true") return "Yes";
  if (value === false || value === "false") return "No";
  return "Unknown";
};

const formatDate = (timestamp) => {
  if (!timestamp?.seconds) return "Just now";

  return new Date(timestamp.seconds * 1000).toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatDistance = (distanceMiles) => {
  if (typeof distanceMiles !== "number") return "";
  if (distanceMiles < 1) return "Less than 1 mile away";
  return `${Math.round(distanceMiles)} miles away`;
};

const geocodeLocation = async (locationText) => {
  if (!locationText.trim()) return null;
  if (!window.google?.maps?.Geocoder) return null;

  const geocoder = new window.google.maps.Geocoder();

  return new Promise((resolve) => {
    geocoder.geocode({ address: locationText.trim() }, (results, status) => {
      if (status !== "OK" || !results?.[0]) {
        resolve(null);
        return;
      }

      resolve(buildBroadLocationFromGoogleResult(results[0], locationText));
    });
  });
};

const getEditablePostDraft = (post = {}) => ({
  title: post.title || "",
  text: post.text || "",
  type: post.type || post.category || "general",
  postType: post.postType || (post.type === "want_ad" ? "want_ad" : "standard"),
  wantedCategory: post.wantedCategory || "farm_goods",
  budgetText: post.budgetText || "",
  urgency: post.urgency || "just_browsing",
  ...normalizeBarterFields(post),
  locationText:
    post.locationText ||
    [post.city, post.state].filter(Boolean).join(", ") ||
    post.county ||
    post.locationNote ||
    "",
  landUseType: post.landUseType || "horse_grazing",
  offeringOrSeeking: post.offeringOrSeeking || "offering",
  acreage: post.acreage || "",
  fenced:
    post.fenced === true || post.fenced === false ? String(post.fenced) : "unknown",
  waterAccess:
    post.waterAccess === true || post.waterAccess === false
      ? String(post.waterAccess)
      : "unknown",
  animalAllowedNotes: post.animalAllowedNotes || "",
  priceType: post.priceType || "contact",
  price: post.price || "",
  landAvailabilityStatus: post.landAvailabilityStatus || "available_now",
  availableStarting: post.availableStarting || "",
  availabilityNote: post.availabilityNote || "",
});

const normalizeUnknownBoolean = (value) => {
  if (value === "true") return true;
  if (value === "false") return false;
  return "unknown";
};

const CommunityPostCard = ({
  post,
  currentUser,
  userProfile,
  distanceMiles,
  isDirectTarget = false,
  commentCountOverride = null,
  commentCountLoading = false,
  onCommentCountChange,
}) => {
  const navigate = useNavigate();
  const [archiving, setArchiving] = useState(false);
  const [resolvingAlert, setResolvingAlert] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [commentCount, setCommentCount] = useState(
    Number(commentCountOverride ?? post.commentCount ?? 0)
  );
  const [helpfulReactions, setHelpfulReactions] = useState([]);
  const [helpfulCount, setHelpfulCount] = useState(Number(post.helpfulCount || 0));
  const [hasReactedHelpful, setHasReactedHelpful] = useState(false);
  const [reactionSaving, setReactionSaving] = useState(false);
  const [shareFeedback, setShareFeedback] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editDraft, setEditDraft] = useState(() => getEditablePostDraft(post));
  const isOwner = currentUser?.uid && post.authorId === currentUser.uid;
  const isAdmin = userProfile?.isAdmin === true || userProfile?.admin === true;
  const locationDebugEnabled =
    typeof window !== "undefined" &&
    ["localhost", "127.0.0.1"].includes(window.location.hostname) &&
    new URLSearchParams(window.location.search || "").get("locationDebug") === "1";

  useEffect(() => {
    if (!isEditing) {
      setEditDraft(getEditablePostDraft(post));
    }
  }, [isEditing, post]);

  useEffect(() => {
    if (typeof commentCountOverride === "number") {
      setCommentCount(commentCountOverride);
    }
  }, [commentCountOverride]);

  useEffect(() => {
    if (!post?.id) return;

    const unsubscribe = onSnapshot(
      collection(db, "communityPosts", post.id, "reactions"),
      (snapshot) => {
        const helpfulReactions = snapshot.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .filter((reaction) => reaction.type === "helpful");

        setHelpfulReactions(helpfulReactions);
        setHelpfulCount(helpfulReactions.length);
        setHasReactedHelpful(
          Boolean(
            currentUser?.uid &&
              helpfulReactions.some((reaction) => reaction.id === currentUser.uid)
          )
        );
      },
      (error) => {
        console.error("Community reactions listener failed:", error);
      }
    );

    return () => {
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, [currentUser?.uid, post?.id]);

  const handleCommentCountChange = useCallback(
    (count) => {
      setCommentCount(count);
      onCommentCountChange?.(count);
    },
    [onCommentCountChange]
  );

  const handleArchive = async () => {
    if (!isOwner) return;
    if (!window.confirm("Archive this community post?")) return;

    try {
      setArchiving(true);
      await updateDoc(doc(db, "communityPosts", post.id), {
        isArchived: true,
        status: "archived",
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Failed archiving community post:", error);
      alert("Could not archive post. Please try again.");
      setArchiving(false);
    }
  };

  const handleResolveAlert = async () => {
    if (!(isOwner || isAdmin)) return;
    if (!window.confirm("Mark this Community Alert as resolved?")) return;

    try {
      setResolvingAlert(true);
      await updateDoc(doc(db, "communityPosts", post.id), {
        alertStatus: "resolved",
        alertResolvedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Failed resolving community alert:", error);
      alert("Could not resolve this alert. Please try again.");
      setResolvingAlert(false);
    }
  };

  const handleEditChange = (event) => {
    const { name, value } = event.target;
    setEditDraft((current) => ({ ...current, [name]: value }));
  };

  const handleEditBarterChange = (nextFields) => {
    setEditDraft((current) => ({
      ...current,
      ...nextFields,
    }));
  };

  const handleSaveEdit = async (event) => {
    event.preventDefault();
    if (!isOwner) return;
    if (!requireVerifiedEmail(currentUser, userProfile)) return;

    const cleanTitle = editDraft.title.trim();
    const cleanText = editDraft.text.trim();

    if (!cleanTitle || !cleanText) {
      alert("Please keep a title and message for this post.");
      return;
    }

    const cleanLocationText = editDraft.locationText.trim();
    const originalLocationText = String(post.locationText || post.locationNote || "").trim();
    const locationChanged = cleanLocationText !== originalLocationText;
    const savedAuthorLocation = normalizeProfileLocation(userProfile, {
      allowFallback: false,
    });
    const currentPostLocation = normalizeLocationRecord(post);
    const hasMissingLocationSource =
      !post.locationSource && !post.source && !post.location?.locationSource && !post.location?.source;
    const hasMissingNestedLocation = !post.location || typeof post.location !== "object";
    const distanceFromAuthorProfile = getSafeDistanceMiles(
      savedAuthorLocation,
      currentPostLocation
    );
    const hasExplicitMappedLandLocation =
      (editDraft.type === "land_use" || post.type === "land_use" || post.category === "land_use") &&
      hasValidCoordinates(currentPostLocation) &&
      ((Array.isArray(post.fields) && post.fields.length > 0) ||
        (Array.isArray(post.polygons) && post.polygons.length > 0));
    const isFarFromAuthorProfile =
      typeof distanceFromAuthorProfile === "number" &&
      distanceFromAuthorProfile > LEGACY_LOCATION_PROFILE_DISTANCE_THRESHOLD_MILES;
    const shouldRepairLegacyLocation =
      !locationChanged &&
      !hasExplicitMappedLandLocation &&
      (hasMissingLocationSource ||
        hasMissingNestedLocation ||
        post.locationMissing === true ||
        isFarFromAuthorProfile);
    const shouldCanonicalizeExplicitMappedLocation =
      !locationChanged &&
      hasExplicitMappedLandLocation &&
      (hasMissingLocationSource || hasMissingNestedLocation);

    if (cleanLocationText) {
      const validation = validateBroadLocationInput(cleanLocationText);
      if (!validation.ok) {
        alert(validation.error || LOCATION_AREA_ERROR);
        return;
      }
    }

    try {
      setEditSaving(true);

      const updatePayload = {
        title: cleanTitle,
        text: cleanText,
        type: editDraft.type || "general",
        postType: editDraft.type === "want_ad" ? "want_ad" : "standard",
        updatedAt: serverTimestamp(),
      };

      if (editDraft.type === "want_ad") {
        updatePayload.wantedCategory = editDraft.wantedCategory || "farm_goods";
        updatePayload.wantedTitle = cleanTitle;
        updatePayload.wantedDescription = cleanText;
        updatePayload.budgetText = String(editDraft.budgetText || "").trim();
        updatePayload.urgency = editDraft.urgency || "just_browsing";
        Object.assign(updatePayload, buildBarterPayload(editDraft));
      } else {
        Object.assign(updatePayload, DEFAULT_BARTER_FIELDS, {
          wantedCategory: "",
          wantedTitle: "",
          wantedDescription: "",
          budgetText: "",
          urgency: "",
        });
      }

      if (editDraft.type === "land_use") {
        updatePayload.description = cleanText;
        updatePayload.landUseType = editDraft.landUseType || "horse_grazing";
        updatePayload.offeringOrSeeking = editDraft.offeringOrSeeking || "offering";
        updatePayload.acreage = String(editDraft.acreage || "").trim();
        updatePayload.fenced = normalizeUnknownBoolean(editDraft.fenced);
        updatePayload.waterAccess = normalizeUnknownBoolean(editDraft.waterAccess);
        updatePayload.animalAllowedNotes = String(editDraft.animalAllowedNotes || "").trim();
        updatePayload.priceType = editDraft.priceType || "contact";
        updatePayload.price = String(editDraft.price || "").trim();
        updatePayload.landAvailabilityStatus =
          editDraft.landAvailabilityStatus || "available_now";
        updatePayload.availableStarting =
          editDraft.landAvailabilityStatus === "available_starting"
            ? editDraft.availableStarting || ""
            : "";
        updatePayload.availabilityNote =
          editDraft.landAvailabilityStatus === "available_starting"
            ? String(editDraft.availabilityNote || "").trim()
            : "";
      }

      if (locationChanged) {
        let lat = null;
        let lng = null;
        let finalLocationText = cleanLocationText;
        let locationDetails = normalizeBroadLocation(
          { locationText: finalLocationText },
          finalLocationText
        );

        if (finalLocationText) {
          const geocoded = await geocodeLocation(finalLocationText);
          if (geocoded) {
            lat = geocoded.lat;
            lng = geocoded.lng;
            locationDetails = normalizeBroadLocation(geocoded, finalLocationText);
            finalLocationText = locationDetails.locationText;
          } else {
            alert("We could not find coordinates for that location area. Please use a nearby city/county and state.");
            setEditSaving(false);
            return;
          }
        }

        const locationPayload = buildCanonicalLocationPayload({
          ...locationDetails,
          lat,
          lng,
          locationText: finalLocationText,
          locationSource: LOCATION_SOURCES.EXPLICIT,
        });

        Object.assign(updatePayload, locationPayload, { locationNote: finalLocationText });
      } else if (shouldRepairLegacyLocation) {
        const repairSource = selectCommunityEditLocation({
          savedProfile: userProfile,
          existingRecord: {
            ...post,
            locationText: cleanLocationText || post.locationText || post.locationNote || "",
          },
        });
        const locationPayload = buildCanonicalLocationPayload(repairSource);
        const finalLocationText = locationPayload.locationText || cleanLocationText;

        Object.assign(updatePayload, locationPayload, { locationNote: finalLocationText });
      } else if (shouldCanonicalizeExplicitMappedLocation) {
        const locationPayload = buildCanonicalLocationPayload({
          ...selectCommunityEditLocation({
            existingRecord: {
              ...post,
              locationText: cleanLocationText || post.locationText || post.locationNote || "",
            },
            preserveExplicitRecordLocation: true,
          }),
        });

        Object.assign(updatePayload, locationPayload, {
          locationNote: locationPayload.locationText || cleanLocationText,
        });
      }

      await updateDoc(doc(db, "communityPosts", post.id), updatePayload);
      setIsEditing(false);
    } catch (error) {
      console.error("Failed editing community post:", error);
      alert("Could not save changes to this post. Please try again.");
    } finally {
      setEditSaving(false);
    }
  };

  const handleHelpfulToggle = async () => {
    if (!currentUser?.uid) {
      alert("Please sign in to give a Hat Tip.");
      return;
    }

    if (!requireVerifiedEmail(currentUser, userProfile)) return;

    try {
      setReactionSaving(true);
      const reactionRef = doc(
        db,
        "communityPosts",
        post.id,
        "reactions",
        currentUser.uid
      );

      if (hasReactedHelpful) {
        await deleteDoc(reactionRef);
      } else {
        await setDoc(reactionRef, {
          type: "helpful",
          userId: currentUser.uid,
          displayName:
            userProfile?.name ||
            currentUser.displayName ||
            "Community member",
          authorPhoto:
            userProfile?.profilePicture ||
            currentUser.photoURL ||
            "",
          authorFarmAnimal: userProfile?.farmAnimal || "",
          authorFarmAnimalSelectionExplicit:
            userProfile?.farmAnimalSelectionExplicit === true,
          createdAt: serverTimestamp(),
        });
      }
    } catch (error) {
      console.error("Failed toggling Hat Tip reaction:", error);
      alert("Could not update Hat Tip right now. Please try again.");
    } finally {
      setReactionSaving(false);
    }
  };

  const handleShare = async () => {
    const url = getCommunityPostUrl(post.id);
    const title = post.title || "Community post";

    try {
      if (navigator.share) {
        await navigator.share({ title, text: post.text || title, url });
        setShareFeedback("Shared");
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        setShareFeedback("Link copied");
      } else {
        window.prompt("Copy this Community post link:", url);
        setShareFeedback("Link ready");
      }

      window.setTimeout(() => setShareFeedback(""), 2200);
    } catch (error) {
      if (error?.name === "AbortError") return;
      console.error("Failed sharing community post:", error);
      alert("Could not share this post right now. Please try again.");
    }
  };

  const locationLabel =
    post.pinned === true &&
    (post.displayAsBrand === true ||
      post.isFounderPost === true ||
      Boolean(post.createdByAdminId) ||
      (post.authorName === "MyFarmingPal" && Boolean(post.createdByAdminId)))
      ? ""
      : post.locationText ||
        [post.city, post.state].filter(Boolean).join(", ") ||
        post.county ||
        post.locationNote ||
        "Location not listed";
  const displaysAsBrand =
    post.isFounderPost === true ||
    post.displayAsBrand === true ||
    (post.authorName === "MyFarmingPal" && Boolean(post.createdByAdminId));
  const displayAuthorName = displaysAsBrand
    ? "MyFarmingPal"
    : post.authorName || "Community member";
  const displayAuthorPhoto = displaysAsBrand ? "" : post.authorPhoto || "";
  const displayAuthorSubtitle = displaysAsBrand
    ? "Official MyFarmingPal Post"
    : Array.isArray(post.authorRoles) && post.authorRoles.length > 0
    ? post.authorRoles.map(getProfileRoleLabel).join(", ")
    : "MyFarmingPal member";
  const authorProfilePath = post.authorId
    ? getProfilePath({ id: post.authorId, uid: post.authorId, name: displayAuthorName })
    : "";
  const canOpenAuthorProfile = Boolean(!displaysAsBrand && post.authorId);
  const hatTipSummary = getHatTipSummary(helpfulReactions, currentUser?.uid);
  const hatTipAvatarReactions = getHatTipAvatarReactions(
    helpfulReactions,
    currentUser?.uid,
    3
  );
  const isPinnedOfficialPost = post.pinned === true && displaysAsBrand;
  const wasEdited =
    typeof post.updatedAt?.seconds === "number" &&
    typeof post.createdAt?.seconds === "number" &&
    post.updatedAt.seconds > post.createdAt.seconds + 1;
  const imagePositionX =
    typeof post.imagePositionX === "number" ? post.imagePositionX : 50;
  const imagePositionY =
    typeof post.imagePositionY === "number" ? post.imagePositionY : 50;
  const isLandUsePost = post.type === "land_use" || post.category === "land_use";
  const isWantAdPost = post.postType === "want_ad" || post.type === "want_ad";
  const isAlertPost = isCommunityAlertPost(post);
  const isActiveAlert = isActiveCommunityAlert(post);
  const isResolvedAlert = isResolvedCommunityAlert(post);
  const canResolveAlert = isAlertPost && isActiveAlert && (isOwner || isAdmin);
  const alertStatusLabel = isAlertPost ? getCommunityAlertStatusLabel(post) : "";
  const barterDetails = normalizeBarterFields(post);
  const hasMappedLand =
    isLandUsePost &&
    ((Array.isArray(post.fields) && post.fields.length > 0) ||
      (Array.isArray(post.polygons) && post.polygons.length > 0) ||
      (typeof post.lat === "number" && typeof post.lng === "number"));
  const locationDiagnostics = locationDebugEnabled
    ? getLocationDiagnostics({
        id: post.id,
        type: "communityPosts",
        locationData: post,
        comparisonLocation: normalizeProfileLocation(userProfile, {
          allowFallback: false,
        }),
      })
    : null;
  const legacyLocationWarning =
    locationDebugEnabled && locationDiagnostics
      ? {
          missingSource: !post.locationSource && !post.source,
          missingNestedLocation: !post.location || typeof post.location !== "object",
          distanceFromCurrentUserProfileMiles:
            locationDiagnostics.computedDistanceMiles ?? null,
          farFromCurrentUserProfile:
            typeof locationDiagnostics.computedDistanceMiles === "number" &&
            locationDiagnostics.computedDistanceMiles >
              LEGACY_LOCATION_PROFILE_DISTANCE_THRESHOLD_MILES,
        }
      : null;

  return (
    <article
      id={`community-post-${post.id}`}
      className={`community-post-card${displaysAsBrand ? " is-official-post" : ""}${
        post.pinned === true ? " is-pinned-post" : ""
      }${showComments ? " has-expanded-comments" : ""}${
        isDirectTarget ? " is-direct-target" : ""
      }`}
      tabIndex={isDirectTarget ? -1 : undefined}
    >
      <div className="community-post-topline">
        <div className="community-post-badges">
          {post.pinned === true && (
            <span className="community-type-badge type-pinned">Pinned</span>
          )}
          {post.isFounderPost === true && (
            <span className="community-type-badge type-founder">
              Official MyFarmingPal Post
            </span>
          )}
          <span className={`community-type-badge type-${post.type || post.category || "general"}`}>
            {getTypeLabel(post.type || post.category)}
          </span>
          {isAlertPost && alertStatusLabel && (
            <span
              className={`community-type-badge type-local-alert-status${
                isResolvedAlert ? " is-resolved" : ""
              }`}
            >
              {alertStatusLabel}
            </span>
          )}
          {isWantAdPost && (
            <span className="community-type-badge type-want_ad">Looking For</span>
          )}
        </div>
        <span>{formatDate(post.createdAt)}</span>
      </div>

      {isEditing ? (
        <form className="community-edit-form" onSubmit={handleSaveEdit}>
          <div className="community-form-grid">
            <label className="form-label">
              Title
              <input
                name="title"
                value={editDraft.title}
                onChange={handleEditChange}
                maxLength={120}
              />
            </label>
            <label className="form-label">
              Category
              <select name="type" value={editDraft.type} onChange={handleEditChange}>
                {COMMUNITY_TYPES.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="form-label">
            Message
            <textarea
              name="text"
              value={editDraft.text}
              onChange={handleEditChange}
              rows={4}
              maxLength={1800}
            />
          </label>

          {editDraft.type === "want_ad" && (
            <section className="want-ad-form-panel">
              <div className="community-form-grid">
                <label className="form-label">
                  Wanted category
                  <select
                    name="wantedCategory"
                    value={editDraft.wantedCategory}
                    onChange={handleEditChange}
                  >
                    {WANTED_CATEGORY_OPTIONS.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="form-label">
                  Budget or trade note optional
                  <input
                    name="budgetText"
                    value={editDraft.budgetText}
                    onChange={handleEditChange}
                    maxLength={120}
                  />
                </label>

                <label className="form-label">
                  Timing
                  <select name="urgency" value={editDraft.urgency} onChange={handleEditChange}>
                    {WANT_AD_URGENCY_OPTIONS.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <BarterExchangeSection
                value={editDraft}
                onChange={handleEditBarterChange}
                title="Cash / trade options"
              />
            </section>
          )}

          {editDraft.type === "land_use" && (
            <section className="land-use-form-panel community-edit-land-panel">
              <div className="community-form-grid">
                <label className="form-label">
                  Land use type
                  <select
                    name="landUseType"
                    value={editDraft.landUseType}
                    onChange={handleEditChange}
                  >
                    {LAND_USE_TYPES.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="form-label">
                  Offering or seeking
                  <select
                    name="offeringOrSeeking"
                    value={editDraft.offeringOrSeeking}
                    onChange={handleEditChange}
                  >
                    <option value="offering">Offering</option>
                    <option value="seeking">Seeking</option>
                  </select>
                </label>

                <label className="form-label">
                  Acreage optional
                  <input
                    name="acreage"
                    value={editDraft.acreage}
                    onChange={handleEditChange}
                  />
                </label>

                <label className="form-label">
                  Price type
                  <select name="priceType" value={editDraft.priceType} onChange={handleEditChange}>
                    <option value="free">Free</option>
                    <option value="paid">Paid</option>
                    <option value="trade">Trade</option>
                    <option value="contact">Contact</option>
                  </select>
                </label>

                <label className="form-label">
                  Fenced
                  <select name="fenced" value={editDraft.fenced} onChange={handleEditChange}>
                    <option value="unknown">Unknown</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </label>

                <label className="form-label">
                  Water access
                  <select
                    name="waterAccess"
                    value={editDraft.waterAccess}
                    onChange={handleEditChange}
                  >
                    <option value="unknown">Unknown</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </label>

                <label className="form-label">
                  Price optional
                  <input name="price" value={editDraft.price} onChange={handleEditChange} />
                </label>

                <label className="form-label">
                  Availability
                  <select
                    name="landAvailabilityStatus"
                    value={editDraft.landAvailabilityStatus}
                    onChange={handleEditChange}
                  >
                    <option value="available_now">Available Now</option>
                    <option value="available_starting">Available Starting</option>
                  </select>
                </label>

                {editDraft.landAvailabilityStatus === "available_starting" && (
                  <label className="form-label">
                    Available starting optional
                    <input
                      type="date"
                      name="availableStarting"
                      value={editDraft.availableStarting}
                      onChange={handleEditChange}
                    />
                  </label>
                )}
              </div>

              <label className="form-label">
                Animal/use notes optional
                <textarea
                  name="animalAllowedNotes"
                  value={editDraft.animalAllowedNotes}
                  onChange={handleEditChange}
                  rows={3}
                />
              </label>

              {editDraft.landAvailabilityStatus === "available_starting" && (
                <label className="form-label">
                  Availability note optional
                  <textarea
                    name="availabilityNote"
                    value={editDraft.availabilityNote}
                    onChange={handleEditChange}
                    rows={3}
                  />
                </label>
              )}
            </section>
          )}

          <label className="form-label">
            Location area
            <input
              name="locationText"
              value={editDraft.locationText}
              onChange={handleEditChange}
              placeholder="County, state or city, state"
            />
            <p className="input-helper">
              Use a general area only. Editing this field updates the post's public
              location area.
            </p>
          </label>

          <div className="community-edit-actions">
            <button type="submit" className="btn-primary" disabled={editSaving}>
              {editSaving ? "Saving..." : "Save changes"}
            </button>
            <button
              type="button"
              className="btn-soft"
              onClick={() => {
                setEditDraft(getEditablePostDraft(post));
                setIsEditing(false);
              }}
              disabled={editSaving}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <>
          <h3>{post.title}</h3>
          <p className="community-post-text">{post.text}</p>
        </>
      )}

      {!isEditing && isWantAdPost && (
        <div className="want-ad-details-box">
          <div className="exchange-badge-row">
            <span className="exchange-badge">Wanted: {getWantedCategoryLabel(post.wantedCategory)}</span>
            {getExchangeBadges(post).map((badge) => (
              <span className="exchange-badge" key={badge}>
                {badge}
              </span>
            ))}
          </div>
          {post.budgetText && (
            <p>
              <strong>Budget/trade:</strong> {post.budgetText}
            </p>
          )}
          {barterDetails.barterEnabled &&
            !barterDetails.barterOpenToAnything &&
            barterDetails.barterCategories.length > 0 && (
              <div className="exchange-badge-row">
                {barterDetails.barterCategories.map((category) => (
                  <span className="exchange-badge" key={category}>
                    {getBarterCategoryLabel(category)}
                  </span>
                ))}
              </div>
            )}
          {barterDetails.barterEnabled && barterDetails.barterNotes && (
            <p>
              <strong>Trade notes:</strong> {barterDetails.barterNotes}
            </p>
          )}
        </div>
      )}

      {!isEditing && isLandUsePost && (
        <div className="land-use-post-panel">
          <div className="land-use-post-heading">
            <span>🧭 Land Share</span>
            <strong>{post.offeringOrSeeking === "seeking" ? "Seeking" : "Offering"}</strong>
            <span>{getLandAvailabilityLabel(post.landAvailabilityStatus || "available_now")}</span>
            {(post.landAvailabilityStatus || "available_now") === "available_starting" &&
              post.availableStarting && (
                <span>
                  Starting {new Date(`${post.availableStarting}T00:00:00`).toLocaleDateString()}
                </span>
              )}
          </div>
          {(post.landAvailabilityStatus || "available_now") === "available_starting" &&
            post.availabilityNote && (
              <p className="availability-note">{post.availabilityNote}</p>
            )}
          <div className="land-use-detail-strip">
            <span>{landUseTypeLabels[post.landUseType] || "Land use"}</span>
            {post.acreage && <span>{post.acreage}</span>}
            <span>Fenced: {formatUnknownBoolean(post.fenced)}</span>
            <span>Water: {formatUnknownBoolean(post.waterAccess)}</span>
            <span>{post.priceType || "contact"}{post.price ? ` · ${post.price}` : ""}</span>
          </div>
          {post.animalAllowedNotes && <p>{post.animalAllowedNotes}</p>}
          <div className="land-use-post-actions">
            <button
              type="button"
              className="btn-soft land-share-details-link"
              onClick={() => navigate("/land-share")}
            >
              Open in Land Share
            </button>
          </div>
          {hasMappedLand && (
            <button
              type="button"
              className="btn-soft land-use-map-link"
              onClick={() =>
                navigate("/map", {
                  state: { focusItemId: post.id, focusItemType: "landUse" },
                })
              }
            >
              View land on map
            </button>
          )}
        </div>
      )}

      {!isEditing && isAlertPost && (
        <div className={`community-alert-inline-note${isResolvedAlert ? " is-resolved" : ""}`}>
          <strong>{alertStatusLabel || "Community Alert"}</strong>
          <span>{getCommunityAlertExpiryLabel(post)}</span>
          <span>Community-submitted alert</span>
        </div>
      )}

      {post.imageUrl && (
        <div className="community-post-image-frame">
          <img
            src={post.imageUrl}
            alt={post.title || "Community post"}
            className="community-post-image"
            style={{
              objectPosition: `${imagePositionX}% ${imagePositionY}%`,
            }}
          />
        </div>
      )}

      {displaysAsBrand && (
        <div className="community-official-author">
          <span className="community-official-icon">MFP</span>
          <div>
            <strong>{displayAuthorSubtitle}</strong>
            {locationLabel && <span>📍 {locationLabel}</span>}
          </div>
        </div>
      )}

      {!displaysAsBrand && (
        <div className="community-post-author">
          <button
            type="button"
            className="community-author-link"
            onClick={() => canOpenAuthorProfile && navigate(authorProfilePath)}
            disabled={!canOpenAuthorProfile}
          >
            <FarmAvatar
              profile={{
                id: post.authorId,
                uid: post.authorId,
                name: displayAuthorName,
                photo: displayAuthorPhoto,
                farmAnimal: post.authorFarmAnimal,
                farmAnimalSelectionExplicit:
                  post.authorFarmAnimalSelectionExplicit === true,
              }}
              className="community-post-author-avatar"
            />

            <span>
              <strong>{displayAuthorName}</strong>
              <span>
                {displayAuthorSubtitle}
                {Number(post.authorReviewCount || 0) > 0
                  ? ` · ${Number(post.authorRating || 0).toFixed(1)} stars`
                  : ""}
              </span>
            </span>
          </button>
        </div>
      )}

      <div className="community-post-meta">
        {!displaysAsBrand && <span>{locationLabel}</span>}
        {!isPinnedOfficialPost && formatDistance(distanceMiles) && (
          <span>{formatDistance(distanceMiles)}</span>
        )}
        {wasEdited && <span>Edited</span>}
        <span>
          {commentCountLoading ? "Loading Comments" : getCommentCountLabel(commentCount)}
        </span>
      </div>

      {hatTipSummary && (
        <div className="hat-tip-social-proof">
          {hatTipAvatarReactions.length > 0 && (
            <div className="hat-tip-avatar-stack" aria-hidden="true">
              {hatTipAvatarReactions.map((reaction) => {
                const displayName =
                  getHatTipDisplayName(reaction) ||
                  (reaction.id === currentUser?.uid || reaction.userId === currentUser?.uid
                    ? "You"
                    : "Community member");

                return (
                  <FarmAvatar
                    key={reaction.id || reaction.userId || displayName}
                    profile={{
                      id: reaction.userId || reaction.id,
                      uid: reaction.userId || reaction.id,
                      name: displayName,
                      photo: reaction.authorPhoto,
                      farmAnimal: reaction.authorFarmAnimal,
                      farmAnimalSelectionExplicit:
                        reaction.authorFarmAnimalSelectionExplicit === true,
                    }}
                    className="hat-tip-avatar"
                  />
                );
              })}
            </div>
          )}
          <span>{hatTipSummary}</span>
        </div>
      )}

      {locationDebugEnabled && (
        <pre
          style={{
            margin: "10px 0",
            padding: 10,
            borderRadius: 10,
            background: "#102014",
            color: "#e6f4ea",
            fontSize: 11,
            whiteSpace: "pre-wrap",
          }}
        >
          {JSON.stringify(
            {
              ...locationDiagnostics,
              authorId: post.authorId || null,
              passedDistanceMiles: distanceMiles ?? null,
              radiusBadgeShown:
                !isPinnedOfficialPost && Boolean(formatDistance(distanceMiles)),
              officialPinnedDistanceHidden: isPinnedOfficialPost,
              legacyLocationWarning,
            },
            null,
            2
          )}
        </pre>
      )}

      <div className="community-post-actions">
        <HatTipReaction
          active={hasReactedHelpful}
          count={helpfulCount}
          disabled={reactionSaving}
          onToggle={handleHelpfulToggle}
        />
        <button
          type="button"
          className="btn-soft"
          onClick={() => setShowComments((current) => !current)}
        >
          {showComments
            ? "Hide comments"
            : commentCountLoading
            ? "Comments"
            : commentCount > 0
            ? `View comments (${commentCount})`
            : "Comment"}
        </button>
        <button type="button" className="btn-soft" onClick={handleShare}>
          Share
        </button>
        {shareFeedback && <span className="community-share-feedback">{shareFeedback}</span>}
      </div>

      <div className="community-post-management-actions">
        {isOwner && !isEditing && (
          <button
            type="button"
            className="btn-soft"
            onClick={() => setIsEditing(true)}
          >
            Edit
          </button>
        )}
        {canResolveAlert && (
          <button
            type="button"
            className="btn-soft community-resolve-alert-btn"
            onClick={handleResolveAlert}
            disabled={resolvingAlert}
          >
            {resolvingAlert ? "Resolving..." : "Resolve Alert"}
          </button>
        )}
        {!isOwner && (
          <ReportButton
            type="communityPost"
            targetId={post.id}
            targetCollection="communityPosts"
            targetOwnerId={post.authorId}
            reportedUserId={post.authorId}
            targetTitle={post.title}
            successMessage="Thanks. This post has been reported for review."
            requireTrusted
            userProfile={userProfile}
            compact
          />
        )}
        {isOwner && (
          <button
            type="button"
            className="btn-danger-soft"
            onClick={handleArchive}
            disabled={archiving}
          >
            {archiving ? "Archiving..." : "Archive"}
          </button>
        )}
      </div>

      {showComments && (
        <CommunityComments
          post={post}
          currentUser={currentUser}
          userProfile={userProfile}
          onCountChange={handleCommentCountChange}
        />
      )}
    </article>
  );
};

export default CommunityPostCard;
